import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeClosed } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import { z } from "zod/v3";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { TextInputField } from "@/components/ui/TextInputField";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address."),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters.")
    .max(128, "Password is too long."),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface EmailLoginFormProps {
  /** Fired when "Continue here" is activated, alongside any `signupHref` nav. */
  onSignupClick: () => void;
  /**
   * Renders "Continue here" as a real link to this path. The full-screen route
   * passes `/signup` so it stays right-clickable; the bottom sheet omits it and
   * gets a plain button that swaps sheets instead of navigating.
   */
  signupHref?: string;
  /** Runs before navigating away to /forgot-password. */
  onForgotPassword?: () => void;
}

/**
 * Email + password sign-in form. Owns its own validation and submit; the
 * surrounding chrome (full-screen header vs. bottom sheet) and the placement of
 * the auth error banner belong to the host, which reads `error` off the auth
 * store itself.
 *
 * Shared by the `/login/email` screen (`EmailLoginMobile`) and the sign-in
 * bottom sheet opened from `/login`.
 */
export function EmailLoginForm({
  onSignupClick,
  signupHref,
  onForgotPassword,
}: EmailLoginFormProps) {
  const { error, signIn, clearError } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const hasAuthError = Boolean(error);
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const handleSubmit = async (values: LoginFormValues) => {
    clearError();
    setSubmitting(true);
    try {
      await signIn(values.email, values.password);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClassName = cn(
    "rounded-lg border-black/20 bg-white text-black placeholder:text-[#626f95]",
    hasAuthError && "border-[#ef4444] shadow-[0_0_0_3px_#fca5a5]",
  );

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex flex-col gap-7"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs font-normal text-black">
                  Email
                </FormLabel>
                <FormControl>
                  <TextInputField
                    {...field}
                    type="email"
                    placeholder="Enter your email"
                    autoComplete="email"
                    size="lg"
                    isInvalid={hasAuthError}
                    inputClassName={fieldClassName}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs font-normal text-black">
                  Password
                </FormLabel>
                <FormControl>
                  <TextInputField
                    {...field}
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter Password"
                    autoComplete="current-password"
                    size="lg"
                    isInvalid={hasAuthError}
                    inputClassName={fieldClassName}
                    endAdornment={
                      <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        className="rounded p-1 text-black/70 hover:bg-black/5"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? (
                          <Eye className="h-4 w-4" />
                        ) : (
                          <EyeClosed className="h-4 w-4" />
                        )}
                      </button>
                    }
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <div className="-mt-1 flex justify-end">
            <Link
              to="/forgot-password"
              onClick={onForgotPassword}
              className="text-muted-foreground rounded-md px-2 py-1 text-xs transition hover:text-black"
            >
              Forgot Password?
            </Link>
          </div>

          <Button
            type="submit"
            variant="vibrant-mobile"
            size="lg-mobile"
            disabled={submitting}
            className="mt-2 w-full text-sm"
          >
            {submitting ? "Logging in..." : "Log in"}
          </Button>
        </form>
      </Form>

      <div className="text-muted-foreground mt-8 text-center text-sm">
        Have an invite code?{" "}
        {signupHref ? (
          <Link
            to={signupHref}
            onClick={onSignupClick}
            className="font-medium text-black underline underline-offset-2"
          >
            Continue here
          </Link>
        ) : (
          <button
            type="button"
            onClick={onSignupClick}
            className="font-medium text-black underline underline-offset-2"
          >
            Continue here
          </button>
        )}
      </div>
    </>
  );
}
